import { useState } from "react";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { validateProfilePasswordChange } from "utils/profilePasswordValidation";
import {
  PasswordSection,
  SectionTitle,
  PasswordActionsGrid,
  ActionCard,
  ActionCardTitle,
  ActionCardDescription,
  PasswordInputWrapper,
  InputLabel,
  PasswordStrengthHint,
  FormActions,
} from "styles/components/userDetails.styles";

interface Props {
  isChangingPassword: boolean;
  onChangePassword: (currentPassword: string, newPassword: string) => void;
}

function ProfilePasswordTab({ isChangingPassword, onChangePassword }: Props) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = () => {
    const error = validateProfilePasswordChange({
      currentPassword,
      newPassword,
      confirmPassword,
    });
    setLocalError(error);
    if (error) return;

    onChangePassword(currentPassword, newPassword);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <PasswordSection style={{ marginTop: "1rem" }}>
      <SectionTitle>
        <i className="pi pi-lock" />
        Change Password
      </SectionTitle>

      <PasswordActionsGrid>
        <ActionCard>
          <ActionCardTitle>
            <i className="pi pi-key" style={{ marginRight: "0.5rem" }} />
            Update Your Password
          </ActionCardTitle>
          <ActionCardDescription>
            Enter your current password, then choose a new one (minimum 8
            characters).
          </ActionCardDescription>

          <PasswordInputWrapper>
            <InputLabel htmlFor="profile-current-password">
              Current Password
            </InputLabel>
            <Password
              inputId="profile-current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              toggleMask
              feedback={false}
              className="w-full"
              inputClassName="w-full"
            />
          </PasswordInputWrapper>

          <PasswordInputWrapper>
            <InputLabel htmlFor="profile-new-password">New Password</InputLabel>
            <Password
              inputId="profile-new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              toggleMask
              feedback
              className="w-full"
              inputClassName="w-full"
            />
            <PasswordStrengthHint>
              Use at least 8 characters.
            </PasswordStrengthHint>
          </PasswordInputWrapper>

          <PasswordInputWrapper>
            <InputLabel htmlFor="profile-confirm-password">
              Confirm New Password
            </InputLabel>
            <Password
              inputId="profile-confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              toggleMask
              feedback={false}
              className="w-full"
              inputClassName="w-full"
            />
          </PasswordInputWrapper>

          {localError && (
            <p style={{ color: "var(--red-500, #ef4444)", marginTop: "0.75rem" }}>
              {localError}
            </p>
          )}

          <FormActions>
            <Button
              label="Change Password"
              icon="pi pi-check"
              onClick={handleSubmit}
              loading={isChangingPassword}
              disabled={isChangingPassword}
            />
          </FormActions>
        </ActionCard>
      </PasswordActionsGrid>
    </PasswordSection>
  );
}

export default ProfilePasswordTab;
